import type { RecordingCapabilitiesV1, SignalWindowV1 } from "@rekonas/domain";

export const PLAYBACK_TILE_SECONDS = 30;
export const PLAYBACK_LOOKAHEAD_SECONDS = 90;
export const MAX_PLAYBACK_BYTES = 24 * 1024 * 1024;
export const MAX_PLAYBACK_REQUESTS = 2;
export const PLAYBACK_RATES = [0.5, 1, 2, 4] as const;
export type PlaybackRate = typeof PLAYBACK_RATES[number];
export type PlaybackStatus = "paused" | "playing" | "seeking" | "buffering" | "ended" | "error";

export interface PlaybackSnapshot {
  status: PlaybackStatus;
  positionSeconds: number;
  rate: PlaybackRate;
  bufferedUntilSeconds: number;
  generation: number;
  requiresResume: boolean;
  error: string | null;
}

export interface PlaybackSource {
  recordingRef: string;
  durationSeconds: number;
  capabilities: RecordingCapabilitiesV1;
  essentialModalities: string[];
  orientationModalities: string[];
  loadCapabilities(signal: AbortSignal): Promise<RecordingCapabilitiesV1>;
  loadWindow(modalities: string[], start: number, duration: 30 | 60, signal: AbortSignal): Promise<SignalWindowV1>;
}

type Tile = {
  essential?: SignalWindowV1;
  orientation?: SignalWindowV1;
  orientationDone?: boolean;
  error?: string;
  bytes: number;
};

type Flight = { controller: AbortController; generation: number };

export function estimateWindowBytes(window: SignalWindowV1): number {
  // A conservative JS heap estimate for parsed arrays, plus the small metadata envelope.
  return 4096 + window.series.reduce((total, series) => total + (series.representation === "native"
    ? series.values.length * 16 + 512
    : series.intervals.length * 96 + 512), 0);
}

export class PlaybackController {
  private snapshot: PlaybackSnapshot = {
    status: "paused", positionSeconds: 0, rate: 1, bufferedUntilSeconds: 0,
    generation: 0, requiresResume: false, error: null,
  };
  private readonly listeners = new Set<() => void>();
  private readonly tiles = new Map<number, Tile>();
  private readonly flights = new Map<string, Flight>();
  private readonly budgetBlocked = new Map<string, number>();
  private validation: Flight | null = null;
  private intent = false;
  private hidden = false;
  private disposed = false;
  private anchorSeconds = 0;
  private anchorMs = 0;
  private frameId: number | null = null;
  private lastFrameMs = Number.NEGATIVE_INFINITY;

  constructor(
    private readonly source: PlaybackSource,
    private readonly clock: {
      now(): number;
      requestFrame(callback: FrameRequestCallback): number;
      cancelFrame(id: number): void;
    } = {
      now: () => performance.now(),
      requestFrame: (callback) => requestAnimationFrame(callback),
      cancelFrame: (id) => cancelAnimationFrame(id),
    },
  ) {}

  getSnapshot = (): PlaybackSnapshot => this.snapshot;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  private publish(changes: Partial<PlaybackSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...changes };
    this.listeners.forEach((listener) => listener());
  }

  private tileStart(seconds: number): number {
    return Math.floor(Math.min(Math.max(0, seconds), Math.max(0, this.source.durationSeconds - 1e-9)) / PLAYBACK_TILE_SECONDS) * PLAYBACK_TILE_SECONDS;
  }

  private tile(start: number): Tile {
    let tile = this.tiles.get(start);
    if (!tile) { tile = { bytes: 0 }; this.tiles.set(start, tile); }
    return tile;
  }

  windowAt(seconds: number): SignalWindowV1 | null {
    return this.tiles.get(this.tileStart(seconds))?.essential || null;
  }

  orientationAt(seconds: number): SignalWindowV1 | null {
    return this.tiles.get(this.tileStart(seconds))?.orientation || null;
  }

  get bufferedBytes(): number {
    return [...this.tiles.values()].reduce((total, tile) => total + tile.bytes, 0);
  }

  get outstandingRequests(): number { return this.flights.size + Number(this.validation != null); }

  private bufferedUntil(): number {
    let start = this.tileStart(this.snapshot.positionSeconds);
    if (!this.tiles.get(start)?.essential) return this.snapshot.positionSeconds;
    while (start < this.source.durationSeconds && this.tiles.get(start)?.essential) start += PLAYBACK_TILE_SECONDS;
    return Math.min(this.source.durationSeconds, start);
  }

  private stopFrame(): void {
    if (this.frameId != null) this.clock.cancelFrame(this.frameId);
    this.frameId = null;
  }

  private requestFrame(): void {
    if (this.frameId != null || this.snapshot.status !== "playing") return;
    this.frameId = this.clock.requestFrame((timestamp) => {
      this.frameId = null;
      if (timestamp - this.lastFrameMs >= 1000 / 30 - 1) {
        this.lastFrameMs = timestamp;
        this.tick(this.clock.now());
      }
      this.requestFrame();
    });
  }

  private abortRequests(): void {
    this.validation?.controller.abort();
    this.validation = null;
    this.flights.forEach((flight) => flight.controller.abort());
    this.flights.clear();
  }

  private evict(): void {
    const position = this.snapshot.positionSeconds;
    for (const [start] of this.tiles) {
      if (start + PLAYBACK_TILE_SECONDS <= position ||
          start > position + PLAYBACK_LOOKAHEAD_SECONDS + PLAYBACK_TILE_SECONDS) this.tiles.delete(start);
    }
  }

  private accepts(window: SignalWindowV1, start: number, duration: 30 | 60): boolean {
    return window.recording_ref === this.source.recordingRef &&
      window.data_version === this.source.capabilities.data_version &&
      window.profile_version === this.source.capabilities.profile_version &&
      window.requested.start_seconds === start && window.requested.duration_seconds === duration;
  }

  private launch(start: number, kind: "essential" | "orientation"): void {
    const id = `${start}:${kind}`;
    const controller = new AbortController();
    const generation = this.snapshot.generation;
    this.flights.set(id, { controller, generation });
    const orientationOnly = kind === "essential" && !this.source.essentialModalities.length;
    const modalities = kind === "essential" && !orientationOnly ? this.source.essentialModalities : this.source.orientationModalities;
    // Pose spans both sides of tile boundaries, so SLERP does not develop a false gap at each edge.
    const requestStart = kind === "orientation" || orientationOnly ? Math.max(0, start - 15) : start;
    const requestDuration = kind === "orientation" || orientationOnly ? 60 : PLAYBACK_TILE_SECONDS;
    void this.source.loadWindow(modalities, requestStart, requestDuration, controller.signal).then((window) => {
      if (this.disposed || controller.signal.aborted || generation !== this.snapshot.generation) return;
      if (!this.accepts(window, requestStart, requestDuration)) throw new TypeError("Obsolete playback response");
      const tile = this.tile(start);
      const bytes = estimateWindowBytes(window);
      this.evict();
      if (kind === "essential" && start === this.tileStart(this.snapshot.positionSeconds)) {
        for (const [futureStart] of this.tiles) {
          if (futureStart > start && this.bufferedBytes + bytes > MAX_PLAYBACK_BYTES) this.tiles.delete(futureStart);
        }
      }
      if (bytes > MAX_PLAYBACK_BYTES) {
        if (kind === "essential") tile.error = "The selected window exceeds the playback memory limit.";
        else tile.orientationDone = true;
      } else if (this.bufferedBytes + bytes > MAX_PLAYBACK_BYTES) {
        if (start === this.tileStart(this.snapshot.positionSeconds) && kind === "essential") {
          tile.error = "The selected window exceeds the playback memory limit.";
        } else {
          // Retry only after eviction makes enough room for this measured response.
          this.budgetBlocked.set(id, bytes);
          if (kind === "essential") this.tiles.delete(start);
        }
      } else {
        tile.bytes += bytes;
        if (kind === "essential") { tile.essential = window; if (orientationOnly) { tile.orientation = window; tile.orientationDone = true; } }
        else { tile.orientation = window; tile.orientationDone = true; }
      }
    }).catch((error: unknown) => {
      if (this.disposed || controller.signal.aborted || generation !== this.snapshot.generation) return;
      const tile = this.tile(start);
      if (kind === "essential") tile.error = error instanceof Error ? error.message : "Playback window failed.";
      else tile.orientationDone = true;
    }).finally(() => {
      if (this.flights.get(id)?.controller !== controller) return;
      this.flights.delete(id);
      if (this.disposed || generation !== this.snapshot.generation) return;
      this.updateAvailability();
      this.schedule();
    });
  }

  private schedule(): void {
    if (!this.intent || this.hidden || this.disposed || this.snapshot.requiresResume) return;
    this.evict();
    const first = this.tileStart(this.snapshot.positionSeconds);
    const limit = Math.min(this.source.durationSeconds, this.snapshot.positionSeconds + PLAYBACK_LOOKAHEAD_SECONDS);
    const currentNeeded = this.budgetBlocked.get(`${first}:essential`);
    if (currentNeeded && !this.tiles.get(first)?.essential) {
      for (const [start] of this.tiles) {
        if (start > first && this.bufferedBytes + currentNeeded > MAX_PLAYBACK_BYTES) this.tiles.delete(start);
      }
    }
    for (let start = first; start < limit && this.flights.size < MAX_PLAYBACK_REQUESTS; start += PLAYBACK_TILE_SECONDS) {
      const tile = this.tiles.get(start);
      if (!tile?.essential && !tile?.error && !this.flights.has(`${start}:essential`)) {
        const needed = this.budgetBlocked.get(`${start}:essential`) || 4 * 1024 * 1024;
        if ((start === first && !this.budgetBlocked.has(`${start}:essential`)) || this.bufferedBytes + needed <= MAX_PLAYBACK_BYTES) {
          this.budgetBlocked.delete(`${start}:essential`);
          this.launch(start, "essential");
        }
      }
      if (tile?.essential && this.source.orientationModalities.length && !tile.orientationDone &&
          !this.flights.has(`${start}:orientation`) && this.flights.size < MAX_PLAYBACK_REQUESTS &&
          this.bufferedBytes + (this.budgetBlocked.get(`${start}:orientation`) || 4 * 1024 * 1024) <= MAX_PLAYBACK_BYTES) {
        this.budgetBlocked.delete(`${start}:orientation`);
        this.launch(start, "orientation");
      }
    }
  }

  private updateAvailability(): void {
    if (!this.intent || this.hidden || this.disposed) return;
    const current = this.tiles.get(this.tileStart(this.snapshot.positionSeconds));
    if (current?.error) {
      this.intent = false;
      this.stopFrame();
      this.publish({ status: "error", error: current.error, bufferedUntilSeconds: this.bufferedUntil() });
      return;
    }
    const bufferedUntilSeconds = this.bufferedUntil();
    if (current?.essential) {
      if (this.snapshot.status !== "playing") {
        this.anchorSeconds = this.snapshot.positionSeconds;
        this.anchorMs = this.clock.now();
      }
      this.publish({ status: "playing", bufferedUntilSeconds, error: null });
      this.requestFrame();
    } else {
      this.stopFrame();
      this.publish({ status: "buffering", bufferedUntilSeconds });
    }
  }

  tick(now = this.clock.now()): void {
    if (this.snapshot.status !== "playing" || this.hidden || this.disposed) return;
    const target = Math.min(this.source.durationSeconds, this.anchorSeconds + Math.max(0, now - this.anchorMs) * this.snapshot.rate / 1000);
    const edge = this.bufferedUntil();
    const positionSeconds = Math.min(target, edge);
    if (positionSeconds >= this.source.durationSeconds) {
      this.intent = false;
      this.stopFrame();
      this.abortRequests();
      this.publish({ positionSeconds: this.source.durationSeconds, bufferedUntilSeconds: this.source.durationSeconds, status: "ended" });
      return;
    }
    if (target >= edge) {
      this.stopFrame();
      const nextError = this.tiles.get(this.tileStart(positionSeconds))?.error;
      if (nextError) {
        this.intent = false;
        this.publish({ positionSeconds, bufferedUntilSeconds: edge, status: "error", error: nextError });
        return;
      }
      this.publish({ positionSeconds, bufferedUntilSeconds: edge, status: "buffering" });
      this.schedule();
      return;
    }
    this.publish({ positionSeconds, bufferedUntilSeconds: edge });
    this.schedule();
  }

  setRate(rate: PlaybackRate): void {
    if (!PLAYBACK_RATES.includes(rate)) return;
    this.tick();
    this.anchorSeconds = this.snapshot.positionSeconds;
    this.anchorMs = this.clock.now();
    this.publish({ rate });
  }

  setModalities(essentialModalities: string[], orientationModalities: string[]): void {
    if (this.disposed ||
        this.source.essentialModalities.join(",") === essentialModalities.join(",") &&
        this.source.orientationModalities.join(",") === orientationModalities.join(",")) return;
    this.stopFrame();
    this.abortRequests();
    this.tiles.clear();
    this.budgetBlocked.clear();
    this.source.essentialModalities = essentialModalities;
    this.source.orientationModalities = orientationModalities;
    const ended = this.snapshot.positionSeconds >= this.source.durationSeconds;
    if ((!essentialModalities.length && !orientationModalities.length) || ended) this.intent = false;
    this.publish({ generation: this.snapshot.generation + 1, bufferedUntilSeconds: this.snapshot.positionSeconds,
      status: ended ? "ended" : this.intent ? "buffering" : "paused", error: null });
    if (this.intent && !this.snapshot.requiresResume) {
      this.schedule();
      this.updateAvailability();
    }
  }

  pause(): void {
    this.tick();
    this.intent = false;
    this.stopFrame();
    this.abortRequests();
    if (this.snapshot.status !== "ended") this.publish({ status: "paused", error: null });
  }

  seek(seconds: number): void {
    if (this.disposed) return;
    const wanted = this.intent;
    this.stopFrame();
    this.abortRequests();
    this.tiles.clear();
    this.budgetBlocked.clear();
    const positionSeconds = Math.min(this.source.durationSeconds, Math.max(0, Number.isFinite(seconds) ? seconds : 0));
    const generation = this.snapshot.generation + 1;
    if (positionSeconds >= this.source.durationSeconds) this.intent = false;
    this.publish({ positionSeconds, generation, bufferedUntilSeconds: positionSeconds,
      status: positionSeconds >= this.source.durationSeconds ? "ended" : wanted ? "seeking" : "paused", error: null });
    if (wanted && positionSeconds < this.source.durationSeconds && !this.snapshot.requiresResume) {
      this.schedule();
      this.updateAvailability();
    }
  }

  /** Revalidate after suspension without advancing the clock or prefetching tiles. */
  async restorePaused(): Promise<boolean> {
    if (this.disposed || this.hidden || this.validation) return false;
    if (this.snapshot.requiresResume) {
      const controller = new AbortController();
      const generation = this.snapshot.generation;
      this.validation = { controller, generation };
      this.publish({ status: "seeking", error: null });
      try {
        const current = await this.source.loadCapabilities(controller.signal);
        if (controller.signal.aborted || this.disposed || this.hidden || generation !== this.snapshot.generation) return false;
        if (current.recording_ref !== this.source.recordingRef ||
            current.data_version !== this.source.capabilities.data_version ||
            current.profile_sha256 !== this.source.capabilities.profile_sha256) {
          this.publish({ status: "error", error: "Recording data changed. Reopen the viewer." });
          return false;
        }
      } catch {
        if (!controller.signal.aborted && !this.disposed) this.publish({ status: "error", error: "Recording access could not be revalidated." });
        return false;
      } finally {
        if (this.validation?.controller === controller) this.validation = null;
      }
      this.publish({ requiresResume: false, status: this.snapshot.positionSeconds >= this.source.durationSeconds ? "ended" : "paused", error: null });
    }
    return true;
  }

  async play(): Promise<void> {
    if (this.disposed || this.hidden || (!this.source.essentialModalities.length && !this.source.orientationModalities.length)) return;
    if (this.snapshot.requiresResume && !await this.restorePaused()) return;
    if (this.disposed || this.hidden) return;
    if (this.snapshot.status === "ended") this.seek(0);
    if (this.snapshot.status === "error") this.seek(this.snapshot.positionSeconds);
    this.intent = true;
    this.publish({ status: "buffering", error: null });
    this.schedule();
    this.updateAvailability();
  }

  suspend(): void {
    if (this.disposed) return;
    this.tick();
    this.hidden = true;
    this.intent = false;
    this.stopFrame();
    this.abortRequests();
    this.tiles.clear();
    this.budgetBlocked.clear();
    this.publish({ status: "paused", requiresResume: true, bufferedUntilSeconds: this.snapshot.positionSeconds });
  }

  visible(): void { this.hidden = false; }

  dispose(): void {
    this.disposed = true;
    this.stopFrame();
    this.abortRequests();
    this.tiles.clear();
    this.budgetBlocked.clear();
    this.listeners.clear();
  }
}
